export type ScheduleTab = 'today' | 'upcoming' | 'past'

/** Anything with a date. Games come from `/games/dates` as bare date strings,
 *  events as `SnEvent`s with an ISO `starts_at`. */
export interface ScheduleTabDates {
  /** `YYYY-MM-DD` strings, already normalized by the caller. */
  gameDates: string[] | null | undefined
  /** ISO instants. Empty/absent when events have not resolved yet. */
  eventStartsAt: (string | null | undefined)[] | null | undefined
}

/**
 * Picks which tab the team schedule opens on.
 *
 * Games win: if the team has a game today the tab is TODAY, and if it has any
 * future game the tab is UPCOMING — events are not consulted at all in either
 * case. Events are only a tie-breaker for the one outcome where games have
 * nothing to say about the present or future, i.e. they would land on PAST.
 * There, a today/future event upgrades the tab so a team whose only remaining
 * activity is an event does not open on a backwards-looking list.
 *
 * `now` is injected rather than read from the clock so the boundary cases
 * (an event later today, an event that just ended) are testable.
 */
export const resolveDefaultScheduleTab = (
  { gameDates, eventStartsAt }: ScheduleTabDates,
  now: Date = new Date()
): ScheduleTab => {
  const startOfToday = new Date(now)
  startOfToday.setHours(0, 0, 0, 0)
  const startOfTomorrow = new Date(startOfToday)
  startOfTomorrow.setDate(startOfTomorrow.getDate() + 1)

  const todayKey = formatDateKey(startOfToday)

  const dates = (gameDates ?? []).filter(Boolean)
  if (dates.includes(todayKey)) {
    return 'today'
  }
  if (dates.some((date) => date > todayKey)) {
    return 'upcoming'
  }

  // Games alone would say PAST — consult events before settling for it.
  const times = (eventStartsAt ?? [])
    .map((iso) => (iso ? new Date(iso).getTime() : NaN))
    .filter((time) => !Number.isNaN(time))

  if (
    times.some(
      (time) =>
        time >= startOfToday.getTime() && time < startOfTomorrow.getTime()
    )
  ) {
    return 'today'
  }
  if (times.some((time) => time >= startOfTomorrow.getTime())) {
    return 'upcoming'
  }

  return 'past'
}

const formatDateKey = (date: Date): string => {
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const day = `${date.getDate()}`.padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}
